/**
 * Wraps form and includes basic functionality e.g. the page title, action buttons and
 * confirmation dialog.
 */
import React, { Component, Children, cloneElement } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import {
  Button,
  Loader,
  Dropdown,
  withApplicationNotificationsContext,
} from '@adc/ui-core'
import { DEFAULT_ERROR_TITLE } from '../../constants/statics'
import { submit as submitValues, requiredIf } from '../../utils'
import PageLayout from '../pageLayout'
import ConfirmationModal from '../confirmationModal'
import UnsavedChangesModal from '../unsavedChangesModal'
import { ActionIcon } from '../dataTable'

/**
 * Proptypes handled by parent page that are passed down to this wrapper
 */
export const pagePropTypes = {
  // dispatchers
  submit: requiredIf(PropTypes.func, props => !props.viewOnly),
  reset: requiredIf(PropTypes.func, props => !props.viewOnly),
  change: requiredIf(PropTypes.func, props => !props.viewOnly),
  updateRecordInList: requiredIf(PropTypes.func, props => !props.viewOnly),
  clearList: requiredIf(PropTypes.func, props => !props.viewOnly),
  // redux-form and redux-router info
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  history: PropTypes.object,
  match: PropTypes.object,
  // no editable input fields should show.
  viewOnly: PropTypes.bool,
}

/**
 * Utility function for pages that use this component.
 * Simply pulls all the redux (form and router) props out...
 */
export const getReduxRelatedProps = props => {
  const dispatchers = _.pick(props, [
    'submit',
    'reset',
    'change',
    'updateRecordInList',
    'clearList',
  ])
  const reduxInfo = _.pick(props, [
    'submitting',
    'pristine',
    'history',
    'match',
  ])

  return { ...dispatchers, ...reduxInfo }
}

class FormWrapper extends Component {
  static propTypes = {
    ...pagePropTypes,
    // form specific data processing
    defaultValues: PropTypes.object.isRequired,
    uiToApi: PropTypes.func.isRequired,
    apiToUi: PropTypes.func.isRequired,
    // providers
    getRecord: PropTypes.func.isRequired,
    postRecord: requiredIf(PropTypes.func, props => !props.viewOnly),
    putRecord: requiredIf(PropTypes.func, props => !props.viewOnly),
    // entity specific ui
    crumbs: PropTypes.array.isRequired, // e.g. [ROOT_CRUMB, VENUE_TEMPLATES_CRUMB]
    entityName: PropTypes.string.isRequired, // e.g. 'Venue Template'
    entityDynamicTitle: PropTypes.node, // e.g. <span>Some dynamic inline stuff</span>
    // Opt out of title overflow:hidden clipping so an interactive title (e.g. a
    // dropdown) can render outside the header. Default preserves string ellipsis.
    titleAllowOverflow: PropTypes.bool,
    entityListRoute: PropTypes.string.isRequired, // e.g. '/venue-templates'
    primaryKey: PropTypes.string.isRequired, // e.g. 'venue_template_id'
    alternativeRoute: PropTypes.string, // Allows to set the url to change when the form is done
    children: PropTypes.node.isRequired, // form component
    showAuditHistory: PropTypes.bool,
    showMoreOptions: PropTypes.bool,
    moreOptions: PropTypes.array,
    customButtons: PropTypes.array,
    cancelButtonText: PropTypes.string,
    saveButtonText: PropTypes.string,
    useMutationResponse: PropTypes.bool, // whether to take the response from an update request and update local state
    notifications: PropTypes.object,
  }

  static defaultProps = {
    viewOnly: false,
    customButtons: [],
    saveButtonText: 'Save',
    moreOptions: [],
    showMoreOptions: false,
    showAuditHistory: false,
    useMutationResponse: true,
    notifications: {},
  }

  constructor(props) {
    super(props)

    this.state = {
      confirmationModalOpen: false,
      unsavedChangesModalOpen: false,
      recordId: null,
      initialValues: props.defaultValues,
      processing: false,
    }
  }

  componentDidMount = () => {
    this.mounted = true
    this.fetchData()
  }

  componentWillUnmount = () => {
    this.mounted = false
  }

  fetchData = async () => {
    const { match, notifications, apiToUi, getRecord } = this.props

    if (match && match.params && !_.isNil(match.params.id)) {
      this.state.recordId = parseInt(match.params.id, 10)
      this.state.processing = true

      try {
        const res = await getRecord(null, match.params.id)
        this.mounted &&
          this.setState({
            initialValues: apiToUi(res),
            processing: false,
          })
      } catch (err) {
        this.mounted && this.setState({ processing: false })
        const { toast } = notifications
        toast?.open({ title: DEFAULT_ERROR_TITLE, error: err })
        this.onDone()
      }
    }
  }

  get isEditMode() {
    // treat as edit mode when id is passed in param AND we've got it into state properly
    const { match } = this.props
    return Boolean(
      match &&
        match.params &&
        !_.isNil(match.params.id) &&
        !_.isNil(this.state.recordId)
    )
  }

  getEntityEditUrl() {
    const { recordId } = this.state
    const { entityListRoute } = this.props
    return `${entityListRoute}/${recordId}/edit`
  }

  onSubmit = async (values, redirectUrl) => {
    const {
      updateRecordInList,
      clearList,
      apiToUi,
      uiToApi,
      putRecord,
      postRecord,
      primaryKey,
      history,
      useMutationResponse,
    } = this.props
    /**
     * Here we pass initialValues to let apps decide if they can spare
     * sending some data if it didn't change
     */
    const apiValues = uiToApi(values, this.state.initialValues)
    const result = await submitValues(
      this.isEditMode ? putRecord : postRecord,
      apiValues
    )

    const recordId = result[primaryKey]
    const newState = {
      recordId,
      confirmationModalOpen: true,
    }

    // By default, set to response result for latest updated info. Apps can decide to ignore an update response if they
    // are doing things like updating multiple objects in a single request, where the response wouldn't map directly to
    // application state.
    //
    // NOTE: This assumes that every destructive action for a record will return the record itself,
    // which isn't always true. It is valid that we can have a collection of items loaded from one source -- an
    // index, most likely -- but the individual records in that list will load data from a different source.
    if (useMutationResponse) {
      newState.initialValues = apiToUi(result)
    }

    this.setState(newState, () => {
      if (this.isEditMode) {
        // list uses API version of values (snake case'd)
        updateRecordInList(result)
      } else {
        // clear out to force reload
        clearList()
      }

      if (_.isString(redirectUrl) && !_.isEmpty(redirectUrl)) {
        const parentEntityPrimaryKey = `${_.camelCase(primaryKey)}=${recordId}`
        const alternativeRoute = `alternativeRoute=${this.getEntityEditUrl()}`
        history.push(
          `${redirectUrl}?${parentEntityPrimaryKey}&${alternativeRoute}`
        )
      }
    })
  }

  closeConfirmationModal = () => {
    this.setState({ confirmationModalOpen: false })
  }

  closeUnsavedChangesModal = () => {
    this.setState({ unsavedChangesModalOpen: false })
  }

  openUnsavedChangesModal = () => {
    this.setState({ unsavedChangesModalOpen: true })
  }

  onCreateAnother = () => {
    const { reset, defaultValues } = this.props
    // go back to default values and reset form
    this.setState({ initialValues: defaultValues }, () => {
      reset()
      this.closeConfirmationModal()
    })
  }

  onKeepEditing = () => {
    const { history, reset, alternativeRoute } = this.props
    if (!this.isEditMode) {
      const alternativeRouteParam = alternativeRoute
        ? `?alternativeRoute=${alternativeRoute}`
        : ''
      history.push(`${this.getEntityEditUrl()}${alternativeRouteParam}`)
    }
    reset()
    this.closeConfirmationModal()
  }

  onDone = () => {
    const { history, entityListRoute, alternativeRoute } = this.props
    const route = alternativeRoute || entityListRoute
    history.push(route)
  }

  render() {
    const {
      submit,
      pristine,
      submitting,
      change,
      crumbs,
      entityName,
      showMoreOptions,
      showAuditHistory,
      moreOptions,
      entityDynamicTitle,
      entityListRoute,
      children,
      history,
      alternativeRoute,
      viewOnly,
      titleAllowOverflow,
      cancelButtonText,
      saveButtonText,
      customButtons,
      customHeader,
      customHeaderProps,
      saveButtonDisabledOverride,
    } = this.props
    const {
      initialValues,
      processing,
      confirmationModalOpen,
      unsavedChangesModalOpen,
      recordId,
    } = this.state

    const title = `${this.isEditMode ? 'Manage' : 'Create'} ${entityName}`
    const crumbsWithTitle = [...crumbs, title]

    const formProps = {
      change,
      onSubmit: this.onSubmit,
      initialValues,
      history,
      isEditMode: this.isEditMode,
    }

    const childrenWithProps = Children.map(children, child =>
      cloneElement(child, formProps)
    )
    const cancelRoute = alternativeRoute || entityListRoute

    const saveButton = viewOnly
      ? []
      : [
          <Button
            role='primary'
            size='small'
            style={{ marginLeft: '0.75em' }}
            disabled={pristine || submitting || saveButtonDisabledOverride}
            loading={submitting}
            onClick={submit}
            elmId='save_button'
            key='save_button'
          >
            {saveButtonText}
          </Button>,
        ]

    const trigger = <ActionIcon size='small' name='ellipsis vertical' />

    const AuditHistoryLink =
      this.isEditMode && showAuditHistory
        ? [
            {
              key: 'user',
              text: 'Audit History',
              onClick: () =>
                history.push(`${entityListRoute}/${recordId}/history`),
              'data-elm-id': 'audit_history',
            },
          ]
        : []
    const options = [...AuditHistoryLink, ...moreOptions]

    const goToCancelRoute = () => {
      history.push(cancelRoute)
    }

    const buttons = [
      ...customButtons,
      ...saveButton,
      <Button
        size='small'
        style={{ marginLeft: '0.75em' }}
        elmId='cancel_button'
        key='cancel_button'
        onClick={
          viewOnly || pristine ? goToCancelRoute : this.openUnsavedChangesModal
        }
      >
        {cancelButtonText ||
          ((this.isEditMode && pristine) || viewOnly ? 'Back' : 'Cancel')}
      </Button>,
      showMoreOptions && (
        <Dropdown
          className='u-ml-1 morpheus-kebab-menu'
          trigger={trigger}
          options={options}
          direction='left'
          icon={null}
          data-elm-id='more_options'
        />
      ),
    ]

    return (
      <PageLayout
        title={
          entityDynamicTitle ||
          `${this.isEditMode ? 'Manage' : 'Create'} ${entityName}`
        }
        crumbs={crumbsWithTitle}
        buttons={buttons}
        customHeader={customHeader}
        customHeaderProps={customHeaderProps}
        titleAllowOverflow={titleAllowOverflow}
      >
        <ConfirmationModal
          entityName={entityName}
          isEditMode={this.isEditMode}
          isModalOpen={confirmationModalOpen}
          recordId={recordId}
          onModalClose={this.closeConfirmationModal}
          onCreateAnother={this.onCreateAnother}
          onKeepEditing={this.onKeepEditing}
          onDone={this.onDone}
        />
        <UnsavedChangesModal
          allowContinueWithoutSaving
          isModalOpen={unsavedChangesModalOpen}
          onContinueWithoutSaving={goToCancelRoute}
          onKeepEditing={this.closeUnsavedChangesModal}
        />
        <Loader active={processing} inverted />
        {childrenWithProps}
      </PageLayout>
    )
  }
}

export const FormWrapperUtils = {
  pagePropTypes,
  getReduxRelatedProps,
}
export { FormWrapper }
export default withApplicationNotificationsContext(FormWrapper)
